import { useEffect, useState } from "react";
import { Lock, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEditor, findSection } from "@/components/editor/useEditorState";
import CostButton from "./CostButton";
import ProposalCard from "./ProposalCard";
import SharedLock from "./SharedLock";
import { useAiEdits } from "./useAiEdits";

function Analysis({ e, hook }) {
  const [sel, setSel] = useState(() => e.analysis.items.filter((i) => !i.shared).map((i) => `${i.type}:${i.id}`));
  const [unlock, setUnlock] = useState(false);
  useEffect(() => { if (!unlock) setSel((s) => s.filter((k) => !e.analysis.items.find((i) => `${i.type}:${i.id}` === k)?.shared)); }, [unlock, e.analysis.items]);
  const items = e.analysis.items.filter((i) => sel.includes(`${i.type}:${i.id}`)).map(({ type, id, reason }) => ({ type, id, reason }));
  const body = items.length ? { items, unlock_shared: unlock } : null;
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-3" data-testid="fix-analysis">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-sky-700">Analisi · {e.label.replace("Correggi: ", "")}</p>
        <p className="mt-1 text-[12.5px] text-slate-800" data-testid="fix-analysis-summary">{e.analysis.summary}</p>
        {e.analysis.question && <p className="mt-1 text-[12px] text-amber-800" data-testid="fix-analysis-question">{e.analysis.question}</p>}
        {e.error && <p className="mt-1 text-[12px] text-red-700">{e.error}</p>}
      </div>
      <div className="space-y-1.5" data-testid="fix-items">
        <p className="text-[11px] font-medium text-slate-600">Elementi da correggere (solo questi verranno modificati)</p>
        {e.analysis.items.map((i) => {
          const k = `${i.type}:${i.id}`, locked = i.shared && !unlock;
          return (
            <label key={k} className={`flex items-start gap-2 rounded-md border bg-white p-2 text-[12px] ${locked ? "opacity-60" : ""}`} data-testid={`fix-item-${i.type}-${i.id}`}>
              <Checkbox checked={sel.includes(k)} disabled={locked} onCheckedChange={(v) => setSel(v ? [...sel, k] : sel.filter((x) => x !== k))} className="mt-0.5" data-testid={`fix-item-check-${i.type}-${i.id}`} />
              <span className="min-w-0">
                <b className="text-slate-900">{i.name}</b>{i.page ? ` · ${i.page}` : ""}{i.shared && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-700"><Lock size={10} />condiviso · {i.pages.length} pagine</span>}
                <span className="block text-slate-600">{i.reason}</span>
              </span>
            </label>
          );
        })}
        {!e.analysis.items.length && <p className="text-[11px] text-slate-500">L'analisi non ha individuato elementi: rispondi alla domanda qui sopra con una nuova descrizione.</p>}
      </div>
      {e.analysis.items.some((i) => i.shared) && <SharedLock on={unlock} setOn={setUnlock} />}
      <CostButton path={`/ai-edits/${e.id}/fix/estimate`} body={body} label="Genera correzione" disabled={!body || !!hook.running} busy={hook.busy === "fix"}
        onRun={(est) => hook.startFix(e.id, body, est)} testid="fix-run-btn" />
      <Button size="sm" variant="ghost" className="h-7 w-full text-xs" onClick={() => hook.discard(e)} data-testid="fix-discard-analysis-btn">Scarta analisi</Button>
    </div>
  );
}

export default function FixPanel() {
  const ed = useEditor();
  const { selection, content, pageId, diagnostics } = ed;
  const hook = useAiEdits(["fix"]);
  const [problem, setProblem] = useState("");
  const [per, setPer] = useState("page");
  const [useDiag, setUseDiag] = useState(true);
  const sec = selection?.kind === "section" ? findSection(content, selection.id) : null;
  const page = content.pages.find((p) => p.id === pageId);
  const perimeter = per === "section" && !sec ? "page" : per;
  const analyzed = hook.edits.find((e) => e.status === "analyzed");
  const others = hook.edits.filter((e) => e.status !== "analyzed" && !(e.status === "discarded" && !e.ops.length)).slice(0, 5);
  const body = problem.trim().length >= 5 ? { kind: "fix", instruction: problem.trim(), scope_type: perimeter, page_id: page?.id || "", section_id: sec?.section.id || "",
    diagnostics: useDiag ? diagnostics.map(({ id, name, message, type }) => ({ id, name, message, type })) : [] } : null;
  return (
    <div className="p-3 space-y-4" data-testid="fix-panel">
      {!analyzed && (
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-[12px] font-medium text-slate-800 flex items-center gap-1.5"><Stethoscope size={14} className="text-sky-600" />Descrivi il problema</p>
            <Textarea rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} placeholder="Es. su mobile il pulsante «Reservar» esce dallo schermo; il riepilogo non mostra il prezzo scelto…" className="text-sm resize-none" data-testid="fix-problem-input" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 shrink-0">Dove cercare</span>
            <Select value={perimeter} onValueChange={setPer}>
              <SelectTrigger className="h-8 text-xs" data-testid="fix-perimeter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sec && <SelectItem value="section">Sezione «{sec.section.name}»</SelectItem>}
                <SelectItem value="page">Pagina «{page?.name}»</SelectItem>
                <SelectItem value="project">Intero progetto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {diagnostics.length > 0 && (
            <label className="flex items-center gap-2 text-[11px] text-slate-700"><Checkbox checked={useDiag} onCheckedChange={(v) => setUseDiag(!!v)} data-testid="fix-use-diagnostics" />Includi i {diagnostics.length} errori rilevati nell'anteprima</label>
          )}
          <CostButton path="/ai-edits/estimate" body={body} label="1 · Analizza il problema" disabled={!body || !!hook.running} busy={hook.busy === "create"}
            onRun={(est) => hook.create(body, est)} testid="fix-analyze-btn" />
          <p className="text-[10.5px] text-slate-500">L'analisi costa poco e individua gli elementi coinvolti: li vedi e scegli tu cosa correggere prima di qualsiasi modifica.</p>
        </div>
      )}
      {analyzed && <Analysis key={analyzed.id} e={analyzed} hook={hook} />}
      <div className="space-y-2" data-testid="fix-proposals">{others.map((e) => <ProposalCard key={e.id} e={e} hook={hook} />)}</div>
    </div>
  );
}
